import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { corpusService, CreateWordData } from '../../services/corpusService';
import { CorpusSector } from '../../types';
import { Button } from '../ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

interface ImportWordsModalProps {
  isOpen: boolean;
  onClose: () => void;
  sectors: CorpusSector[];
  defaultSectorId?: number;
}

export default function ImportWordsModal({
  isOpen,
  onClose,
  sectors,
  defaultSectorId,
}: ImportWordsModalProps) {
  const queryClient = useQueryClient();
  const [selectedSectorId, setSelectedSectorId] = useState(defaultSectorId || sectors[0]?.id || 0);
  const [fileContent, setFileContent] = useState<string>('');
  const [format, setFormat] = useState<'json' | 'txt'>('json');

  const importMutation = useMutation({
    mutationFn: (data: { sectorId?: number; words: CreateWordData[] }) =>
      corpusService.importWords(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['corpus-words'] });
      queryClient.invalidateQueries({ queryKey: ['corpus-stats'] });

      const { results } = data;
      if (results.errors.length > 0) {
        toast.success(
          `Importación completada: ${results.created} creadas, ${results.skipped} omitidas`,
          { duration: 5000 }
        );
      } else {
        toast.success(`${results.created} palabras importadas exitosamente`);
      }

      resetForm();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al importar palabras');
    },
  });

  const resetForm = () => {
    setFileContent('');
    setFormat('json');
    setSelectedSectorId(defaultSectorId || sectors[0]?.id || 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!fileContent.trim()) {
      toast.error('Por favor, carga un archivo o pega el contenido');
      return;
    }

    try {
      let words: CreateWordData[] = [];

      if (format === 'json') {
        const jsonData = JSON.parse(fileContent);
        const wordsArray = Array.isArray(jsonData) ? jsonData : jsonData.words;

        if (!Array.isArray(wordsArray)) {
          throw new Error('El formato JSON debe ser un array de palabras');
        }

        words = wordsArray.map((item: any) => ({
          word: item.word,
          normalizedWord: item.normalizedWord,
          mcerlLevel: item.mcerlLevel || 'B2',
          syllableCount: item.syllableCount,
          complexityScore: item.complexityScore,
          frequencyScore: item.frequencyScore,
          partOfSpeech: item.partOfSpeech,
          lemma: item.lemma,
          stem: item.stem,
          wordType: item.wordType || 'GENERAL',
        }));
      }

      if (words.length === 0) {
        toast.error('No se encontraron palabras válidas en el archivo');
        return;
      }

      importMutation.mutate({
        sectorId: selectedSectorId,
        words,
      });
    } catch (error: any) {
      toast.error(`Error al procesar el archivo: ${error.message}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-green-600 text-white p-6 rounded-t-xl flex items-center justify-between">
          <h2 className="text-2xl font-bold">Importar Palabras</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Sector de Destino
            </label>
            <select
              value={selectedSectorId}
              onChange={(e) => setSelectedSectorId(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              {sectors.map((sector) => (
                <option key={sector.id} value={sector.id}>
                  {sector.icon} {sector.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Pegar Contenido JSON
            </label>
            <textarea
              value={fileContent}
              onChange={(e) => setFileContent(e.target.value)}
              rows={12}
              placeholder='[{"word": "ejemplo", "normalizedWord": "ejemplo", "mcerlLevel": "B2"}]'
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono text-sm"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" onClick={onClose} variant="outline" className="px-6">
              Cancelar
            </Button>
            <Button
              type="submit"
              className="px-6 bg-green-600 hover:bg-green-700"
              disabled={importMutation.isPending}
            >
              {importMutation.isPending ? 'Importando...' : 'Importar Palabras'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
